import { useCallback, useEffect, useRef, useState } from "react";
import { useFetch } from "../useFetch";
import type { Message } from "../../core/types/control";
import { connectSocket } from "../../core/socket/socketClient";

// ============================================
// SOCKET RESPONSE PAYLOAD TYPES
// ============================================

type SocketErrorPayload = {
	success: false;
	message: string;
	timestamp?: string;
};

type ConnectedPayload = {
	message: string;
	socketId: string;
	timestamp: string;
};

type RegisteredPayload = {
	success: true;
	userId: number;
	socketId: string;
	onlineUserIds?: number[];
	message: string;
	timestamp: string;
};

type UserOnlinePayload = {
	userId: number;
	socketId?: string;
	timestamp: string;
};

type UserOfflinePayload = {
	userId: number;
	timestamp: string;
};

type MessageSentPayload = {
	success: true;
	message: string;
	data: Message;
	timestamp: string;
};

type MessageReceivedPayload = {
	success: true;
	message: string;
	timestamp: string;
};

type OrderMessagePayload = {
	orderId: number;
	message: string;
	data: Message;
	timestamp: string;
};

type JoinedOrderRoomPayload = {
	success: true;
	orderId: number;
	room: string;
	message: string;
	timestamp: string;
};

type LeftOrderRoomPayload = {
	success: true;
	orderId: number;
	room: string;
	message: string;
	timestamp: string;
};

type MarkedAsReadPayload = {
	success: true;
	message: string;
	data: Message;
	timestamp: string;
};

type MessageReadPayload = {
	messageId: number;
	readerId: number;
	message: string;
	timestamp: string;
};

type ChatFinalizedPayload = {
	orderId: number;
	message: string;
	timestamp: string;
};

// ============================================
// INTERNAL HELPERS
// ============================================

/**
 * Hook for socket actions that emit an event and wait for a success or error response.
 * Cleans up pending listeners on unmount and on each new execution.
 */
function useSocketAction<TData, TPayload>(
	emitEvent: string,
	successEvent: string,
	errorEvent: string,
) {
	const isMountedRef = useRef(true);
	const activeHandlersRef = useRef<{
		success: ((r: TData) => void) | null;
		error: ((r: SocketErrorPayload) => void) | null;
	}>({ success: null, error: null });

	const [data, setData] = useState<TData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			const socket = connectSocket();
			if (activeHandlersRef.current.success) {
				socket.off(successEvent, activeHandlersRef.current.success);
				activeHandlersRef.current.success = null;
			}
			if (activeHandlersRef.current.error) {
				socket.off(errorEvent, activeHandlersRef.current.error);
				activeHandlersRef.current.error = null;
			}
		};
	}, [successEvent, errorEvent]);

	const execute = useCallback(
		(payload: TPayload): Promise<TData | null> =>
			new Promise((resolve, reject) => {
				const socket = connectSocket();

				// Cancel any pending listeners from a previous call
				if (activeHandlersRef.current.success)
					socket.off(successEvent, activeHandlersRef.current.success);
				if (activeHandlersRef.current.error)
					socket.off(errorEvent, activeHandlersRef.current.error);

				if (isMountedRef.current) {
					setLoading(true);
					setError(null);
				}

				const onSuccess = (response: TData) => {
					socket.off(errorEvent, onError);
					activeHandlersRef.current = { success: null, error: null };
					if (isMountedRef.current) {
						setData(response);
						setLoading(false);
					}
					resolve(response);
				};

				const onError = (response: SocketErrorPayload) => {
					socket.off(successEvent, onSuccess);
					activeHandlersRef.current = { success: null, error: null };
					const message = response?.message ?? "Error desconocido";
					if (isMountedRef.current) {
						setError(message);
						setLoading(false);
					}
					reject(new Error(message));
				};

				activeHandlersRef.current = {
					success: onSuccess as (r: TData) => void,
					error: onError,
				};
				socket.once(successEvent, onSuccess);
				socket.once(errorEvent, onError);
				socket.emit(emitEvent, payload);
			}),
		[emitEvent, successEvent, errorEvent],
	);

	const reset = useCallback(() => {
		if (isMountedRef.current) {
			setData(null);
			setError(null);
			setLoading(false);
		}
	}, []);

	return { data, error, loading, execute, reset };
}

/**
 * Hook for persistent socket event listeners.
 * Subscribes on mount, unsubscribes on unmount.
 * Uses a ref for the callback so re-renders don't cause re-subscriptions.
 */
function useSocketEvent<TPayload>(event: string, callback: (payload: TPayload) => void) {
	const callbackRef = useRef(callback);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(() => {
		const socket = connectSocket();
		const handler = (payload: TPayload) => callbackRef.current(payload);
		socket.on(event, handler);
		return () => {
			socket.off(event, handler);
		};
	}, [event]);
}

// ============================================
// HTTP — GET ALL MESSAGES
// ============================================
export function useMessages(options?: { auto?: boolean }) {
	return useFetch<Message[]>({
		endpoint: "/message",
		method: "GET",
		auto: options?.auto ?? true,
	});
}

// ============================================
// HTTP — GET ALL MESSAGES BY USER (ownership-safe)
// ============================================
export function useMessagesByUser(userId: number, options?: { auto?: boolean }) {
	return useFetch<Message[]>({
		endpoint: `/message/all/user/${userId}`,
		method: "GET",
		auto: options?.auto ?? userId > 0,
	});
}

// ============================================
// HTTP — GET MESSAGE BY ID
// ============================================
export function useMessage(id: number) {
	return useFetch<Message>({
		endpoint: `/message/${id}`,
		method: "GET",
	});
}

// ============================================
// HTTP — GET MESSAGES BY ORDER
// ============================================
export function useMessagesByOrder(orderId: number) {
	return useFetch<Message[]>({
		endpoint: `/message/order/${orderId}`,
		method: "GET",
		auto: orderId > 0,
	});
}

// ============================================
// HTTP — UPDATE MESSAGE
// ============================================
type UpdateMessageDto = Partial<{
	reminent_id: number;
	recipient_id: number;
	order_id: number;
	content_message: string;
}>;

export function useUpdateMessage(id: number) {
	const fetch = useFetch<Message, UpdateMessageDto>({
		endpoint: `/message/${id}`,
		method: "PATCH",
		auto: false,
	});
	const update = (data: UpdateMessageDto) => fetch.execute({ body: data });
	return { ...fetch, update };
}

// ============================================
// HTTP — DELETE MESSAGE
// ============================================
export function useDeleteMessage() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/message",
		method: "DELETE",
		auto: false,
	});
	const remove = (id: number) => fetch.execute({ endpoint: `/message/${id}` });
	return { ...fetch, remove };
}

// ============================================
// HTTP — CHECK ORDER EXISTS
// ============================================
export function useCheckOrderExists(orderId: number) {
	return useFetch<{ exists: boolean }>({
		endpoint: `/message/order/${orderId}/exists`,
		method: "GET",
	});
}

// ============================================
// HTTP — CHECK USER EXISTS
// ============================================
export function useCheckUserExists(userId: number) {
	return useFetch<{ exists: boolean }>({
		endpoint: `/message/user/${userId}/exists`,
		method: "GET",
	});
}

// ============================================
// HTTP — UPLOAD FILE TO MESSAGE
// Use this before useSendMessageWithAttachment.
// Returns { url } to pass as archive_url.
// ============================================
export function useUploadMessageFile(orderId: number) {
	const fetch = useFetch<{
		url: string;
		size: number;
		mimetype: string;
		orderId: number;
		timestamp: string;
	}>({
		endpoint: `/message/upload/${orderId}`,
		method: "PATCH",
		auto: false,
	});
	const upload = (file: File) => fetch.execute({ files: { archive_url: file } });
	return { ...fetch, upload };
}

// ============================================
// WS — REGISTER USER
// Must be called before sending messages.
// Emits: register → { userId }
// Listens: registered | registrationError
// ============================================
export function useRegisterUser() {
	const action = useSocketAction<RegisteredPayload, { userId: number }>(
		"register",
		"registered",
		"registrationError",
	);
	const register = (userId: number) => action.execute({ userId });
	return { ...action, register };
}

// ============================================
// WS — JOIN ORDER ROOM
// Must be called before sending messages in an order chat.
// Emits: joinOrderRoom → { orderId }
// Listens: joinedOrderRoom | joinRoomError
// ============================================
export function useJoinOrderRoom() {
	const action = useSocketAction<JoinedOrderRoomPayload, { orderId: number }>(
		"joinOrderRoom",
		"joinedOrderRoom",
		"joinRoomError",
	);
	const join = (orderId: number) => action.execute({ orderId });
	return { ...action, join };
}

// ============================================
// WS — LEAVE ORDER ROOM
// Emits: LeaveOrderRoom → { orderId }
// Listens: leftOrderRoom | leaveRoomError
// ============================================
export function useLeaveOrderRoom() {
	const action = useSocketAction<LeftOrderRoomPayload, { orderId: number }>(
		"LeaveOrderRoom",
		"leftOrderRoom",
		"leaveRoomError",
	);
	const leave = (orderId: number) => action.execute({ orderId });
	return { ...action, leave };
}

// ============================================
// WS — SEND MESSAGE
// Emits: sendMessage → SendMessageDto
// Listens: messageSent | messageError
// ============================================
type SendMessageDto = {
	reminent_id: number;
	recipient_id: number;
	order_id: number;
	content_message: string;
	archive_url?: string;
};

type SendQuotationMessageItemDto = {
	description_item: string;
	unit_price_item: number;
	quantity_item: number;
	subtotal_item: number;
	picture_item?: string;
};

type SendQuotationMessageDto = {
	reminent_id: number;
	recipient_id?: number;
	order_id: number;
	provider_id: number;
	proposed_price: number;
	description_quotation: string;
	estimated_time: number;
	materials_included: boolean;
	items: SendQuotationMessageItemDto[];
};

export function useSendMessage() {
	const action = useSocketAction<MessageSentPayload, SendMessageDto>(
		"sendMessage",
		"messageSent",
		"messageError",
	);
	const send = (data: SendMessageDto) => action.execute(data);
	return { ...action, send };
}

// ============================================
// WS — SEND MESSAGE WITH ATTACHMENT
// Upload the file first with useUploadMessageFile, then pass the
// returned URL as archive_url.
// Emits: sendMessageWithAttachment → SendMessageDto (archive_url required)
// Listens: messageSent | messageError
// ============================================
export function useSendMessageWithAttachment() {
	const action = useSocketAction<MessageSentPayload, SendMessageDto>(
		"sendMessageWithAttachment",
		"messageSent",
		"messageError",
	);
	const sendWithAttachment = (data: SendMessageDto & { archive_url: string }) =>
		action.execute(data);
	return { ...action, sendWithAttachment };
}

export function useSendQuotationMessage() {
	const action = useSocketAction<MessageSentPayload, SendQuotationMessageDto>(
		"sendQuotationMessage",
		"messageSent",
		"messageError",
	);
	const sendQuotationMessage = (data: SendQuotationMessageDto) => action.execute(data);
	return { ...action, sendQuotationMessage };
}

// ============================================
// WS — MARK MESSAGE AS READ
// Emits: markAsRead → { messageId, userId }
// Listens: markedAsRead | markAsReadError
// ============================================
export function useMarkAsRead() {
	const action = useSocketAction<
		MarkedAsReadPayload,
		{ messageId: number; userId: number }
	>("markAsRead", "markedAsRead", "markAsReadError");
	const markAsRead = (messageId: number, userId: number) =>
		action.execute({ messageId, userId });
	return { ...action, markAsRead };
}

// ============================================
// WS — FINALIZE CHAT
// Closes the order room for all participants.
// Emits: finalChat → { orderId }
// Listens: chatFinalized | finalChatError
// ============================================
export function useFinalChat() {
	const action = useSocketAction<ChatFinalizedPayload, { orderId: number }>(
		"finalChat",
		"chatFinalized",
		"finalChatError",
	);
	const finalize = (orderId: number) => action.execute({ orderId });
	return { ...action, finalize };
}

// ============================================
// WS LISTENERS — Persistent event subscriptions
// ============================================

/** Fires once when the socket connection is established. */
export function useOnConnected(callback: (payload: ConnectedPayload) => void) {
	useSocketEvent<ConnectedPayload>("connected", callback);
}

/** Fires when any user comes online (registers with the gateway). */
export function useOnUserOnline(callback: (payload: UserOnlinePayload) => void) {
	useSocketEvent<UserOnlinePayload>("userOnline", callback);
}

/** Fires when any user disconnects from the gateway. */
export function useOnUserOffline(callback: (payload: UserOfflinePayload) => void) {
	useSocketEvent<UserOfflinePayload>("userOffline", callback);
}

/**
 * Fires on the recipient's socket when someone sends them a message.
 * Use this to show a notification or refresh the message list.
 */
export function useOnMessageReceived(
	callback: (payload: MessageReceivedPayload) => void,
) {
	useSocketEvent<MessageReceivedPayload>("messageReceived", callback);
}

/**
 * Fires on every socket in the order room when a new message is sent.
 * Use this for real-time chat updates inside an order conversation.
 */
export function useOnOrderMessage(callback: (payload: OrderMessagePayload) => void) {
	useSocketEvent<OrderMessagePayload>("orderMessage", callback);
}

/**
 * Fires on the sender's socket when the recipient marks their message as read.
 */
export function useOnMessageRead(callback: (payload: MessageReadPayload) => void) {
	useSocketEvent<MessageReadPayload>("messageRead", callback);
}

/**
 * Fires on all sockets in the order room when the chat is finalized.
 * After this event, no more messages can be sent in that room.
 */
export function useOnChatFinalized(callback: (payload: ChatFinalizedPayload) => void) {
	useSocketEvent<ChatFinalizedPayload>("chatFinalized", callback);
}

/** Fires when a message action fails (e.g. not joined to room, invalid session). */
export function useOnMessageError(callback: (payload: SocketErrorPayload) => void) {
	useSocketEvent<SocketErrorPayload>("messageError", callback);
}
